import React from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Printer, X } from "lucide-react";

const hoje = () => new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });

const Conteudo = ({ stats, mensal, unidades, ano, usuario, testid = "relatorio-conteudo" }) => (
  <div className="px-8 py-7 bg-white text-slate-900" data-testid={testid}>
    <div className="flex items-start justify-between border-b-2 border-primary pb-3">
      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500">e-Salvador · Gabinete</p>
        <h1 className="text-2xl font-semibold font-display tracking-tight mt-1">
          Relatório de Controle de Processos
        </h1>
      </div>
      <div className="text-right text-[11px] text-slate-500 mono leading-relaxed">
        <p>Emitido em {hoje()}</p>
        <p>Responsável: {usuario}</p>
        <p>Exercício: {ano}</p>
      </div>
    </div>

    <h2 className="text-[11px] uppercase tracking-wider text-slate-500 mt-6 mb-2">Números gerais</h2>
    <div className="grid grid-cols-5 gap-2">
      {[
        ["Processos", stats?.total],
        ["Concluídos", stats?.concluidos],
        ["Em andamento", stats?.em_andamento],
        [`Atrasados +${stats?.alerta_dias || 30}d`, stats?.atrasados],
        ["Sindicâncias", stats?.sindicancias],
      ].map(([l, v]) => (
        <div key={l} className="border border-slate-300 p-3">
          <p className="text-[9px] uppercase tracking-wider text-slate-500 leading-tight">{l}</p>
          <p className="text-xl font-semibold font-display mt-1">{(v ?? 0).toLocaleString("pt-BR")}</p>
        </div>
      ))}
    </div>

    {mensal && (
      <>
        <h2 className="text-[11px] uppercase tracking-wider text-slate-500 mt-6 mb-2">
          Processos tramitados por mês — {mensal.ano} (total {mensal.total.toLocaleString("pt-BR")})
        </h2>
        <div className="border border-slate-300 p-4">
          <div className="flex items-end gap-1.5 h-36">
            {mensal.meses.map((m) => {
              const max = Math.max(1, ...mensal.meses.map((x) => x.count));
              return (
                <div key={m.mes} className="flex-1 flex flex-col items-center justify-end h-full">
                  <span className="text-[9px] mono text-slate-500 mb-0.5">{m.count || ""}</span>
                  <div
                    className="w-full"
                    style={{
                      height: `${Math.max(Math.round((m.count / max) * 100), m.count ? 3 : 0)}%`,
                      background: "#004282",
                      WebkitPrintColorAdjust: "exact",
                      printColorAdjust: "exact",
                    }}
                  />
                  <span className="text-[8px] mono text-slate-500 mt-1">{m.mes}</span>
                </div>
              );
            })}
          </div>
        </div>
      </>
    )}

    {stats?.by_status?.length > 0 && (
      <>
        <h2 className="text-[11px] uppercase tracking-wider text-slate-500 mt-6 mb-2">Por status</h2>
        <table className="w-full text-xs border border-slate-300">
          <thead>
            <tr className="text-left bg-slate-100">
              <th className="px-2 py-1.5 border-b border-slate-300">Status</th>
              <th className="px-2 py-1.5 border-b border-slate-300 text-right">Quantidade</th>
              <th className="px-2 py-1.5 border-b border-slate-300 text-right">%</th>
            </tr>
          </thead>
          <tbody>
            {stats.by_status.map((s) => (
              <tr key={s.label} className="border-t border-slate-200">
                <td className="px-2 py-1">{s.label}</td>
                <td className="px-2 py-1 text-right mono">{s.count.toLocaleString("pt-BR")}</td>
                <td className="px-2 py-1 text-right mono">
                  {Math.round((s.count / Math.max(1, stats.total)) * 100)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )}

    {unidades?.length > 0 && (
      <>
        <h2 className="text-[11px] uppercase tracking-wider text-slate-500 mt-6 mb-2">
          Produtividade por unidade de destino
        </h2>
        <table className="w-full text-xs border border-slate-300">
          <thead>
            <tr className="text-left bg-slate-100">
              <th className="px-2 py-1.5 border-b border-slate-300">Unidade</th>
              <th className="px-2 py-1.5 border-b border-slate-300 text-right">Recebidos</th>
              <th className="px-2 py-1.5 border-b border-slate-300 text-right">Concluídos</th>
              <th className="px-2 py-1.5 border-b border-slate-300 text-right">Pendentes</th>
              <th className="px-2 py-1.5 border-b border-slate-300 text-right">Taxa</th>
              <th className="px-2 py-1.5 border-b border-slate-300 text-right">Tempo médio</th>
            </tr>
          </thead>
          <tbody>
            {unidades.map((u) => (
              <tr key={u.unidade} className="border-t border-slate-200">
                <td className="px-2 py-1">{u.unidade}</td>
                <td className="px-2 py-1 text-right mono">{u.recebidos.toLocaleString("pt-BR")}</td>
                <td className="px-2 py-1 text-right mono">{u.concluidos.toLocaleString("pt-BR")}</td>
                <td className="px-2 py-1 text-right mono">{u.pendentes.toLocaleString("pt-BR")}</td>
                <td className="px-2 py-1 text-right mono">{u.taxa}%</td>
                <td className="px-2 py-1 text-right mono">
                  {u.tempo_medio != null ? `${u.tempo_medio} d` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )}

    <p className="text-[9px] text-slate-500 mt-6 pt-2 border-t border-slate-300">
      Documento gerado automaticamente pelo Sistema de Controle de Processos do e-Salvador.
      Todos os acessos e modificações são registrados na trilha de auditoria.
    </p>
  </div>
);

export const RelatorioDialog = ({ open, onOpenChange, stats, mensal, unidades, ano, usuario }) => (
  <>
    {open &&
      createPortal(
        <div id="print-root" data-testid="relatorio-print-root">
          <Conteudo stats={stats} mensal={mensal} unidades={unidades} ano={ano} usuario={usuario} testid="relatorio-conteudo-print" />
        </div>,
        document.body,
      )}

    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[96vw] max-h-[92vh] overflow-y-auto p-0" data-testid="relatorio-dialog">
        <DialogTitle className="sr-only">Relatório de processos</DialogTitle>
        <DialogDescription className="sr-only">
          Relatório consolidado pronto para impressão.
        </DialogDescription>

        <div className="flex items-center justify-between px-6 py-3 border-b border-border sticky top-0 bg-white z-10">
          <p className="text-sm font-medium font-display">Relatório para impressão</p>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => {
                const anterior = document.title;
                document.title = `AFS Track - Relatorio ${new Date().toISOString().slice(0, 10)}`;
                window.print();
                setTimeout(() => { document.title = anterior; }, 1000);
              }}
              data-testid="print-report-btn"
              className="h-9 bg-primary hover:bg-[#003366]"
            >
              <Printer className="h-4 w-4 mr-2" /> Imprimir / Salvar PDF
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="close-report-btn">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Conteudo stats={stats} mensal={mensal} unidades={unidades} ano={ano} usuario={usuario} />
      </DialogContent>
    </Dialog>
  </>
);
